import { useState, useEffect, useCallback } from "react";
import { Database, Play, ArrowClockwise, Check, Warning, Lightning, Article, CalendarBlank } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "@/App";

export default function DataManagement() {
  const [seedResult, setSeedResult] = useState(null);
  const [seedLoading, setSeedLoading] = useState(false);
  const [crawlerStatus, setCrawlerStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [crawlerStarting, setCrawlerStarting] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState(null);
  const [analysisStarting, setAnalysisStarting] = useState(false);
  const [calendar, setCalendar] = useState(null);
  const [blogGenerating, setBlogGenerating] = useState(null);

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await axios.get(`${API}/admin/crawler-status`);
      setCrawlerStatus(res.data);
    } catch (e) {
      console.error(e);
    }
    setStatusLoading(false);
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Fetch analysis gen status
  const fetchAnalysisStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/analysis-gen-status`);
      setAnalysisStatus(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchAnalysisStatus(); }, [fetchAnalysisStatus]);

  // Auto-refresh while crawler or analysis is running
  useEffect(() => {
    if (crawlerStatus?.crawler?.running || analysisStatus?.running) {
      const interval = setInterval(() => { fetchStatus(); fetchAnalysisStatus(); }, 5000);
      return () => clearInterval(interval);
    }
  }, [crawlerStatus?.crawler?.running, analysisStatus?.running, fetchStatus, fetchAnalysisStatus]);

  const seedData = async () => {
    setSeedLoading(true);
    setSeedResult(null);
    try {
      await axios.post(`${API}/admin/seed-data`, {}, { timeout: 10000 });
      // Start polling seed status
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/admin/seed-status`);
          setSeedResult(res.data);
          if (!res.data.running) {
            clearInterval(poll);
            setSeedLoading(false);
            fetchStatus();
          }
        } catch {}
      }, 3000);
    } catch (e) {
      // Even if the POST request fails/times out, the seed may be running in background
      setSeedResult({ progress: "Seed started — checking status...", results: {} });
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/admin/seed-status`);
          setSeedResult(res.data);
          if (!res.data.running && res.data.progress === "Complete") {
            clearInterval(poll);
            setSeedLoading(false);
            fetchStatus();
          }
        } catch {}
      }, 3000);
    }
  };

  const startCrawler = async (stage) => {
    setCrawlerStarting(true);
    try {
      await axios.post(`${API}/admin/run-crawler`, { stage });
      setTimeout(fetchStatus, 1000);
    } catch (e) {
      console.error(e);
    }
    setCrawlerStarting(false);
  };

  const c = crawlerStatus?.collections || {};
  const cr = crawlerStatus?.crawler || {};

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Database size={24} weight="bold" className="text-[#ccff00]" />
        <h1 className="font-heading text-3xl text-white uppercase">DATA MANAGEMENT</h1>
      </div>

      {/* Collection Stats */}
      <div className="border border-[#262626] bg-[#171717] p-5 mb-6" data-testid="collection-stats">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-bold text-white uppercase">DATABASE COLLECTIONS</h2>
          <button onClick={fetchStatus} disabled={statusLoading} className="text-[10px] text-[#888] hover:text-white flex items-center gap-1">
            <ArrowClockwise size={12} className={statusLoading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            { label: "Champion Rosters", count: c.champion_rosters, target: 6976 },
            { label: "Trades", count: c.sleeper_trades, target: 167265 },
            { label: "Drafts", count: c.sleeper_drafts, target: 7000 },
            { label: "Trade Progress", count: c.trade_crawl_progress, target: 6976 },
            { label: "Draft Progress", count: c.draft_crawl_progress, target: 6976 },
          ].map(item => (
            <div key={item.label} className="border border-[#262626] bg-[#0a0a0a] p-3 text-center">
              <p className="font-mono text-lg font-bold text-white">{(item.count ?? "—").toLocaleString()}</p>
              <p className="text-[9px] text-[#555] uppercase">{item.label}</p>
              {item.count > 0 && item.target > 0 && (
                <div className="h-1 bg-[#1a1a1a] rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-[#ccff00] rounded-full" style={{ width: `${Math.min(100, (item.count / item.target) * 100)}%` }} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Seed Data */}
      <div className="border border-[#262626] bg-[#171717] p-5 mb-6" data-testid="seed-data-section">
        <h2 className="text-xs font-bold text-white uppercase mb-2">STEP 1: SEED ALL DATA</h2>
        <p className="text-[10px] text-[#888] mb-4">
          Loads <strong className="text-white">everything</strong> into production MongoDB: championship analysis, draft ADP, archetypes, deadline analysis,
          PLUS all 6,976 champion rosters, 167K real trades, and 7K drafts from compressed data files.
          This is a one-time operation that takes about 30-60 seconds.
        </p>
        <button
          onClick={seedData}
          disabled={seedLoading}
          className="h-9 px-5 bg-[#ccff00] text-black text-[11px] font-bold rounded-sm hover:bg-[#b8e600] transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="seed-data-btn"
        >
          {seedLoading ? <ArrowClockwise size={14} className="animate-spin" /> : <Lightning size={14} weight="fill" />}
          {seedLoading ? "Seeding..." : "SEED ALL DATA"}
        </button>
        {(seedLoading || seedResult) && (
          <div className="mt-4 space-y-2">
            {seedResult?.running && (
              <div className="flex items-center gap-2 text-[11px] text-[#ccff00]">
                <ArrowClockwise size={12} className="animate-spin" /> {seedResult.progress}
              </div>
            )}
            {seedResult?.progress === "Complete" && !seedResult?.running && (
              <div className="flex items-center gap-2 text-[11px] text-[#4ade80]">
                <Check size={12} weight="bold" /> Seed complete
              </div>
            )}
            {seedResult?.error && (
              <div className="flex items-center gap-2 text-[11px] text-[#ff3b30]">
                <Warning size={12} weight="bold" /> {seedResult.error}
              </div>
            )}
            {seedResult?.results && Object.entries(seedResult.results).map(([key, val]) => (
              <div key={key} className="flex items-center gap-2 text-[11px]">
                {String(val).startsWith("OK") ? <Check size={12} className="text-[#4ade80]" weight="bold" /> : String(val).startsWith("SKIP") ? <span className="text-[#888]">—</span> : <Warning size={12} className="text-[#ff3b30]" weight="bold" />}
                <span className="text-[#888] font-mono">{key}:</span>
                <span className={String(val).startsWith("OK") ? "text-[#4ade80]" : String(val).startsWith("SKIP") ? "text-[#888]" : "text-[#ff3b30]"}>{val}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Step 2: Run Crawlers */}
      <div className="border border-[#262626] bg-[#171717] p-5 mb-6" data-testid="crawler-section">
        <h2 className="text-xs font-bold text-white uppercase mb-2">STEP 2: RUN CRAWLERS (OPTIONAL)</h2>
        <p className="text-[10px] text-[#888] mb-4">
          Crawls Sleeper API for real trade and draft data from champion leagues. This takes 60-90 minutes and runs in background.
          <strong className="text-white"> Requires champion_rosters in DB</strong> — run Seed Data first.
        </p>

        {cr.running && (
          <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-4 rounded-sm mb-4" data-testid="crawler-running">
            <div className="flex items-center gap-2 mb-1">
              <ArrowClockwise size={14} className="text-[#ccff00] animate-spin" />
              <span className="text-xs font-bold text-[#ccff00] uppercase">RUNNING: {cr.stage}</span>
            </div>
            <p className="text-[11px] text-[#888]">{cr.progress}</p>
            <p className="text-[9px] text-[#555] mt-1">Started: {cr.started_at}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => startCrawler("trades")}
            disabled={cr.running || crawlerStarting}
            className="h-9 px-4 border border-[#333] text-white text-[11px] font-bold rounded-sm hover:border-[#ccff00] transition-colors disabled:opacity-40 flex items-center gap-2"
            data-testid="crawl-trades-btn"
          >
            <Play size={12} weight="fill" /> CRAWL TRADES
          </button>
          <button
            onClick={() => startCrawler("drafts")}
            disabled={cr.running || crawlerStarting}
            className="h-9 px-4 border border-[#333] text-white text-[11px] font-bold rounded-sm hover:border-[#ccff00] transition-colors disabled:opacity-40 flex items-center gap-2"
            data-testid="crawl-drafts-btn"
          >
            <Play size={12} weight="fill" /> CRAWL DRAFTS
          </button>
          <button
            onClick={() => startCrawler("all")}
            disabled={cr.running || crawlerStarting}
            className="h-9 px-4 border border-[#ccff00] text-[#ccff00] text-[11px] font-bold rounded-sm hover:bg-[#ccff00]/10 transition-colors disabled:opacity-40 flex items-center gap-2"
            data-testid="crawl-all-btn"
          >
            <Play size={12} weight="fill" /> RUN FULL PIPELINE
          </button>
        </div>

        {cr.stage === "complete" && !cr.running && (
          <div className="mt-4 flex items-center gap-2 text-[11px] text-[#4ade80]">
            <Check size={14} weight="bold" /> Pipeline complete
          </div>
        )}
        {cr.stage === "error" && !cr.running && (
          <div className="mt-4 flex items-center gap-2 text-[11px] text-[#ff3b30]">
            <Warning size={14} weight="bold" /> {cr.progress}
          </div>
        )}
      </div>

      {/* Step 3: Generate Comparison Analyses */}
      <div className="border border-[#262626] bg-[#171717] p-5 mb-6" data-testid="analysis-gen-section">
        <h2 className="text-xs font-bold text-white uppercase mb-2">STEP 3: GENERATE COMPARISON ANALYSES</h2>
        <p className="text-[10px] text-[#888] mb-4">
          Generates AI-written analysis for player comparison pages that are currently missing it.
          Uses GPT to create case-for-each-player, verdicts, and FAQ for every comparison pair.
          Runs in background. ~1.5 seconds per comparison.
        </p>

        {analysisStatus && (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="border border-[#262626] bg-[#0a0a0a] p-3 text-center">
              <p className="font-mono text-lg font-bold text-white">{(analysisStatus.total_in_db ?? 0).toLocaleString()}</p>
              <p className="text-[9px] text-[#555] uppercase">Analyses in DB</p>
            </div>
            <div className="border border-[#262626] bg-[#0a0a0a] p-3 text-center">
              <p className="font-mono text-lg font-bold text-[#4ade80]">{analysisStatus.generated ?? 0}</p>
              <p className="text-[9px] text-[#555] uppercase">Generated This Run</p>
            </div>
            <div className="border border-[#262626] bg-[#0a0a0a] p-3 text-center">
              <p className="font-mono text-lg font-bold text-[#ff3b30]">{analysisStatus.errors ?? 0}</p>
              <p className="text-[9px] text-[#555] uppercase">Errors</p>
            </div>
          </div>
        )}

        {analysisStatus?.running && (
          <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-4 rounded-sm mb-4">
            <div className="flex items-center gap-2 mb-1">
              <ArrowClockwise size={14} className="text-[#ccff00] animate-spin" />
              <span className="text-xs font-bold text-[#ccff00] uppercase">GENERATING...</span>
            </div>
            <p className="text-[11px] text-[#888]">{analysisStatus.current}</p>
            {analysisStatus.total > 0 && (
              <div className="h-1.5 bg-[#1a1a1a] rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-[#ccff00] rounded-full transition-all" style={{ width: `${Math.min(100, ((analysisStatus.generated + analysisStatus.errors) / analysisStatus.total) * 100)}%` }} />
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            onClick={async () => {
              setAnalysisStarting(true);
              try {
                await axios.post(`${API}/admin/generate-all-analyses`, { top_n: 150, pairs_per_player: 8, delay: 1.5 });
                setTimeout(fetchAnalysisStatus, 2000);
              } catch (e) { console.error(e); }
              setAnalysisStarting(false);
            }}
            disabled={analysisStatus?.running || analysisStarting}
            className="h-9 px-5 bg-[#ccff00] text-black text-[11px] font-bold rounded-sm hover:bg-[#b8e600] transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="gen-analyses-btn"
          >
            {analysisStarting ? <ArrowClockwise size={14} className="animate-spin" /> : <Play size={14} weight="fill" />}
            GENERATE MISSING ANALYSES
          </button>
          <button
            onClick={fetchAnalysisStatus}
            className="h-9 px-4 border border-[#333] text-[#888] text-[11px] rounded-sm hover:border-[#ccff00] transition-colors flex items-center gap-1"
          >
            <ArrowClockwise size={12} /> Refresh
          </button>
        </div>

        {analysisStatus?.current && !analysisStatus?.running && analysisStatus.generated > 0 && (
          <p className="text-[11px] text-[#4ade80] mt-3 flex items-center gap-1"><Check size={12} weight="bold" /> {analysisStatus.current}</p>
        )}
      </div>

      {/* Step 4: Content Calendar */}
      <div className="border border-[#262626] bg-[#171717] p-5 mb-6" data-testid="content-calendar">
        <h2 className="text-xs font-bold text-white uppercase mb-2">STEP 4: BLOG CONTENT CALENDAR</h2>
        <p className="text-[10px] text-[#888] mb-4">
          Generate weekly blog posts from live value data. The server automatically publishes a Buy Low / Sell High column every Monday at 10 AM UTC.
          You can also manually trigger posts below.
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { label: "BUY LOW / SELL HIGH", endpoint: "blog/generate-buy-sell", color: "#ccff00" },
            { label: "WEEKLY VALUE MOVERS", endpoint: "blog/generate-weekly-movers", color: "#45b7d1" },
          ].map(({ label, endpoint, color }) => (
            <button
              key={endpoint}
              onClick={async () => {
                setBlogGenerating(endpoint);
                try {
                  const res = await axios.post(`${API}/${endpoint}`);
                  setBlogGenerating(null);
                  // Refresh calendar
                  const cal = await axios.get(`${API}/blog/content-calendar`);
                  setCalendar(cal.data);
                  alert(`Published: ${res.data.title || res.data.slug}`);
                } catch (e) {
                  setBlogGenerating(null);
                  alert(`Error: ${e.response?.data?.detail || e.message}`);
                }
              }}
              disabled={!!blogGenerating}
              className="h-9 px-4 border text-[11px] font-bold rounded-sm transition-colors disabled:opacity-40 flex items-center gap-2"
              style={{ borderColor: color, color }}
            >
              {blogGenerating === endpoint ? <ArrowClockwise size={12} className="animate-spin" /> : <Article size={12} weight="fill" />}
              {label}
            </button>
          ))}

          <button
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/blog/content-calendar`);
                setCalendar(res.data);
              } catch {}
            }}
            className="h-9 px-3 border border-[#333] text-[#888] text-[11px] rounded-sm hover:border-[#ccff00] flex items-center gap-1"
          >
            <CalendarBlank size={12} /> VIEW CALENDAR
          </button>
        </div>

        {calendar && (
          <div>
            <p className="text-[10px] text-[#555] mb-2">Total blog posts: {calendar.total_posts} | Auto-generated: {calendar.auto_generated?.length || 0}</p>
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {calendar.auto_generated?.map((p, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 bg-[#0a0a0a] border border-[#262626] rounded-sm">
                  <div className="min-w-0">
                    <p className="text-[11px] text-white font-semibold truncate">{p.title}</p>
                    <p className="text-[9px] text-[#555]">{p.category} · {new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-[9px] text-[#ccff00] no-underline shrink-0 ml-2">View</a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Step 5: Daily Digest */}
      <div className="border border-[#262626] bg-[#171717] p-5 mb-6" data-testid="daily-digest">
        <h2 className="text-xs font-bold text-white uppercase mb-2">STEP 5: DAILY DIGEST EMAIL</h2>
        <p className="text-[10px] text-[#888] mb-4">
          Sends a metrics summary email to all admins daily at 8 AM UTC.
          Includes new users, logins, tool uses, newsletter signups, and all-time totals.
        </p>
        <button
          onClick={async () => {
            try {
              const res = await axios.post(`${API}/admin/send-digest`);
              alert(`Digest sent to: ${res.data.recipients?.join(", ")}`);
            } catch (e) {
              alert(`Error: ${e.response?.data?.detail || e.message}`);
            }
          }}
          className="h-9 px-4 border border-[#ccff00] text-[#ccff00] text-[11px] font-bold rounded-sm hover:bg-[#ccff00]/10 transition-colors flex items-center gap-2"
          data-testid="send-digest-btn"
        >
          <Lightning size={12} weight="fill" /> SEND DIGEST NOW
        </button>
      </div>

      <p className="text-[10px] text-[#444]">
        Note: The full championship roster crawl (~7,000 leagues) is a multi-hour process best run as a standalone script.
        The trade and draft crawlers depend on champion_rosters existing in the database. If you see 0 champion rosters after seeding,
        the championship data needs to be crawled first using the championship analyzer script.
      </p>
    </div>
  );
}
